import { formatStatValueCompact } from "../../../lib/format";

export interface ChoroplethLegendController {
  element: HTMLElement;
  pill: HTMLElement;
  setVisible: (visible: boolean) => void;
  setRange: (min: number, max: number, type?: string) => void;
  setColors: (lowHex: string, highHex: string) => void;
  destroy: () => void;
}

const formatValue = (value: number, type?: string, isMobile?: boolean): string => {
  const t = (type || "count").toLowerCase();
  // TODO: Percent stats are mixed (ratio vs percent points); formatters accept both.
  if (isMobile && t === "percent") {
    const percentValue = value <= 1 ? value * 100 : value;
    return `${Math.round(percentValue)}%`;
  }
  if (isMobile && t === "percent_change") {
    const pct = value * 100;
    const sign = value > 0 ? "+" : "";
    return `${sign}${Math.round(pct)}%`;
  }
  return formatStatValueCompact(value, t);
};

const blendHexWithWhite = (hex: string, colorWeight = 0.72): string => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return hex;
  const value = parseInt(match[1], 16);
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  const mix = (channel: number) => Math.round(channel * colorWeight + 255 * (1 - colorWeight));
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

export const createChoroplethLegend = (
  isMobile?: boolean,
  onSettingsClick?: () => void,
): ChoroplethLegendController => {
  const wrapper = document.createElement("div");
  // Wrapper is positioned by parent legend row
  wrapper.className = "pointer-events-none";

  const pill = document.createElement("div");
  pill.className = [
    "pointer-events-auto relative inline-flex items-center gap-3 rounded-lg border px-3 py-1.5 text-xs font-medium cursor-pointer",
    "bg-white/90 text-slate-600 border-slate-200 shadow-sm backdrop-blur-sm",
    "dark:bg-slate-900/80 dark:text-slate-300 dark:border-slate-700",
    "sm:py-1.5 py-2.5", // more vertical padding for mobile
  ].join(" ");

  const minGroup = document.createElement("div");
  minGroup.className = "flex items-center gap-2";
  const minDot = document.createElement("span");
  minDot.className = "h-2.5 w-2.5 flex-shrink-0 rounded-full ring-1 ring-black/5 dark:ring-white/10";
  const minLabel = document.createElement("span");
  minLabel.className = "tabular-nums";
  minGroup.appendChild(minDot);
  minGroup.appendChild(minLabel);

  const sep = document.createElement("span");
  sep.textContent = "–";
  sep.className = "opacity-60";

  const maxGroup = document.createElement("div");
  maxGroup.className = "flex items-center gap-2";
  const maxDot = document.createElement("span");
  maxDot.className = "h-2.5 w-2.5 flex-shrink-0 rounded-full ring-1 ring-black/5 dark:ring-white/10";
  const maxLabel = document.createElement("span");
  maxLabel.className = "tabular-nums";
  maxGroup.appendChild(maxDot);
  maxGroup.appendChild(maxLabel);

  pill.appendChild(minGroup);
  pill.appendChild(sep);
  pill.appendChild(maxGroup);
  wrapper.appendChild(pill);

  if (onSettingsClick && !isMobile) {
    pill.addEventListener("click", (e) => {
      e.stopPropagation();
      onSettingsClick();
    });
  }

  const setVisible = (visible: boolean) => {
    wrapper.classList.toggle("hidden", !visible);
  };

  const setRange = (min: number, max: number, type?: string) => {
    minLabel.textContent = formatValue(min, type, isMobile);
    maxLabel.textContent = formatValue(max, type, isMobile);
  };

  const setColors = (lowHex: string, highHex: string) => {
    // Map fills are partially transparent, so blend swatches slightly with white
    // to keep legend dots perceptually aligned with on-map choropleth colors.
    minDot.style.backgroundColor = blendHexWithWhite(lowHex);
    maxDot.style.backgroundColor = blendHexWithWhite(highHex);
  };

  const destroy = () => {
    wrapper.remove();
  };

  setVisible(false);

  return { element: wrapper, pill, setVisible, setRange, setColors, destroy };
};
